// Shared constants and helpers used across pages
import { useEffect, useState } from 'react';

export const CATEGORY_META = {
  stayInformed:      { label: 'Stay Informed',       cls: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700' },
  planForChange:     { label: 'Plan for Change',      cls: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700' },
  preventOrFixIssue: { label: 'Prevent or Fix Issue', cls: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700' },
};

export const SEVERITY_CLS = {
  normal:   'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  high:     'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  critical: 'bg-red-100 text-red-700 font-semibold dark:bg-red-900/30 dark:text-red-400',
};

export function fmt(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

export function getSummary(message) {
  return message.details?.find(d => d.name === 'Summary')?.value ?? null;
}

export function getDetail(message, name) {
  return message.details?.find(d => d.name === name)?.value ?? null;
}

export function truncateSeoText(value, maxLength) {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  if (text.length <= maxLength) return text;

  const candidate = text.slice(0, maxLength - 1).trimEnd();
  const lastSpace = candidate.lastIndexOf(' ');
  const boundary = lastSpace >= Math.floor(maxLength * 0.7) ? lastSpace : candidate.length;
  return `${candidate.slice(0, boundary).trimEnd()}…`;
}

export function buildMessageSeoTitle(message) {
  const title = String(message?.title ?? '').replace(/^\s*\(updated\)\s*/i, '').trim();
  return `${message?.id}: ${title}`;
}

export function buildMessageSeoDescription(message, summary) {
  const fallback = 'Microsoft 365 Message Center update with planned changes, new features, service impact, dates, and administrator actions.';
  return truncateSeoText(`${message?.id}: ${summary ?? fallback}`, 160);
}

// ── Action required countdown ─────────────────────────────────────────────────

export function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
}

export function ActionCountdownBadge({ dateStr, size = 'sm' }) {
  const days = daysUntil(dateStr);
  if (days === null) return null;
  const isLg = size === 'lg';
  let label, cls;
  if (days < 0) {
    const n = Math.abs(days);
    label = isLg ? `${n} day${n !== 1 ? 's' : ''} overdue` : `${n}d ago`;
    cls = 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  } else if (days === 0) {
    label = 'Due today';
    cls = 'bg-red-600 text-white';
  } else if (days <= 7) {
    label = isLg ? `${days} day${days !== 1 ? 's' : ''} left` : `${days}d`;
    cls = 'bg-red-600 text-white';
  } else if (days <= 30) {
    label = isLg ? `${days} days left` : `${days}d`;
    cls = 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
  } else {
    label = isLg ? `${days} days left` : `${days}d`;
    cls = 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
  }
  const sizeCls = isLg ? 'text-sm px-2.5 py-1' : 'text-xs px-2 py-0.5';
  return <span className={`font-semibold ${sizeCls} ${cls}`}>{label}</span>;
}

// ── Dark mode ─────────────────────────────────────────────────────────────────

export function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try {
      const stored = localStorage.getItem('theme');
      if (stored === 'dark') return true;
      if (stored === 'light') return false;
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    try { localStorage.setItem('theme', dark ? 'dark' : 'light'); } catch {}
  }, [dark]);
  return [dark, setDark];
}

// ── Shared per-page meta hook ─────────────────────────────────────────────────

function setMeta(name, content, attr = 'name') {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(url) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

export function usePageMeta({ title, description, url }) {
  useEffect(() => {
    const prev = {
      title:   document.title,
      desc:    document.querySelector('meta[name="description"]')?.content,
      canonical: document.querySelector('link[rel="canonical"]')?.href,
      ogTitle: document.querySelector('meta[property="og:title"]')?.content,
      ogDesc:  document.querySelector('meta[property="og:description"]')?.content,
      ogUrl:   document.querySelector('meta[property="og:url"]')?.content,
      twTitle: document.querySelector('meta[name="twitter:title"]')?.content,
      twDesc:  document.querySelector('meta[name="twitter:description"]')?.content,
    };
    document.title = title;
    setMeta('description', description);
    setCanonical(url);
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:url', url, 'property');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    return () => {
      document.title = prev.title;
      if (prev.desc)    setMeta('description', prev.desc);
      if (prev.canonical) setCanonical(prev.canonical);
      if (prev.ogTitle) setMeta('og:title', prev.ogTitle, 'property');
      if (prev.ogDesc)  setMeta('og:description', prev.ogDesc, 'property');
      if (prev.ogUrl)   setMeta('og:url', prev.ogUrl, 'property');
      if (prev.twTitle) setMeta('twitter:title', prev.twTitle);
      if (prev.twDesc)  setMeta('twitter:description', prev.twDesc);
    };
  }, [title, description, url]);
}
